import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { authAPI, searchAPI } from "services/apis.service";
import { useDispatch } from "react-redux";
import { setsearch, setsidebarDataCopy } from "context/features/dataSlice";
import { loginUser, logoutUser } from "context/features/authSlice";
import { toast } from "react-toastify";
import CustomLoader from "components/base/loader";
import { useAuth, consumeOAuthSignupContext } from "hooks/use-auth.hook";

const SIGNIN_TIMEOUT_MS = 30000; // give up on a hung ClasslinkSignIn call
const STUCK_WARNING_MS = 15000; // log if we're still on the spinner by then

export default function ClassLinkLogin() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // ClassLink codes are single-use; only exchange once.
  const handledRef = useRef(false);
  // Sent to both ClasslinkSignIn and client-logs so backend can match them.
  const attemptId = useRef(
    window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  ).current;
  const startedAt = useRef(Date.now()).current;

  const { logAuthError } = useAuth({ handleLoading: () => {}, toast });

  const log = (error, level = "Error") =>
    logAuthError(error, "classlink", "Redirect/OAuth", level, {
      loginAttemptId: attemptId,
      elapsedMs: Date.now() - startedAt,
      classlinkError: new URLSearchParams(location.search).get("error"),
    });

  const setSidebar = async (data) => {
    try {
      const sidebar = await axios.post(
        searchAPI.sidebar,
        {
          callingUserId: data.userId,
          userId: data.userId,
          includeDeletedRecords: 0,
        },
        {
          headers: {
            Authorization: `Bearer ${data.token}`,
          },
        }
      );

      dispatch(setsidebarDataCopy(sidebar.data));
    } catch (e) {
      console.error(e);
    }
  };

  const handleClasslinkLogin = async (provider) => {
    const code = new URLSearchParams(location.search).get("code");

    if (!code) {
      log("ClassLink redirect missing code", "Warning");
      toast.error("Cannot login in at classlink.");
      navigate("/");
      return;
    }

    dispatch(logoutUser());
    localStorage.removeItem("user");
    dispatch(setsearch(""));
    try {
      const res = await axios.post(
        `${authAPI.classlinkLogin}`,
        {
          code,
          provider,
          loginAttemptId: attemptId,
          ...consumeOAuthSignupContext(),
        },
        { timeout: SIGNIN_TIMEOUT_MS }
      );
      localStorage.setItem("user", JSON.stringify(res.data));
      localStorage.setItem("login", true);
      setSidebar(res.data);

      dispatch(loginUser({ user: res.data }));
      localStorage.setItem("email", res.data.email);
      localStorage.setItem("classlink", code);
      navigate("/");
    } catch (error) {
      console.error("Error sending the code to backend:", error);
      log(error);
      toast.error("There was an issue logging in with Classlink.");
      navigate("/");
    }
  };

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    handleClasslinkLogin("classlink");
  }, [location.search]);

  // This page unmounts as soon as login succeeds or fails, which clears the
  // timer — so if it fires, the user is still staring at the spinner.
  useEffect(() => {
    const timer = setTimeout(
      () => log(`ClassLink login still pending after ${STUCK_WARNING_MS}ms`, "Warning"),
      STUCK_WARNING_MS
    );
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="circular-loader-center">
      <CustomLoader />
    </div>
  );
};
